/**
 * Pure keyword classifier for Risk Analyst (no Supabase / LLM).
 * Used by tryAnswerEarlySignalsFromDb and utterance fixtures.
 */
import {
  categoryFromRoleAsk,
  categoryKeyForPhrase,
  extractSupplierListFilters,
  highestRiskScope,
  isHighestRiskQuestion,
  isSupplierBrandsAsk,
  matchListTableIntent,
} from './earlySignalsDbIntent'
import {
  bareSupplierNameCandidate,
  extractSupplierNameCandidate,
  isDimensionCommentaryAsk,
  isExplicitScorecardAsk,
  isSupplierScorecardAsk,
  supplierBesideDimension,
  urgentSupplierName,
} from './supplierRiskRatingsTable'

export type RiskAnalystTool =
  | 'supplier_brands'
  | 'supplier_scorecard'
  | 'supplier_dimension_commentary'
  | 'list_active_mitigations'
  | 'list_alerts'
  | 'list_suppliers'
  | 'list_tracker'
  | 'list_reports'
  | 'supplier_summary'
  | 'count_by_band'
  | 'count_by_region'
  | 'count_total_suppliers'
  | 'count_filtered_suppliers'
  | 'group_suppliers_by'
  | 'mitigations_for_supplier'
  | 'supplier_highest_areas'
  | 'highest_in_category'
  | 'category_next_action'
  | 'spend_portfolio'
  | 'spend_supplier'
  | 'spend_category'

export type RiskAnalystNarrative = 'next_best_actions' | 'questions_to_ask'

export type RiskAnalystClassification =
  | { kind: 'tool'; tool: RiskAnalystTool; supplierName: string | null; categoryName?: string | null }
  | { kind: 'narrative'; tool: RiskAnalystNarrative; supplierName: string | null }
  | { kind: 'fallthrough' }

function isNextBestAsk(t: string): boolean {
  return t.includes('next best') || t.includes('next-best') || t.includes('best actions')
}

function isQuestionsToAsk(t: string): boolean {
  return (
    t.includes('questions should') ||
    t.includes('what questions') ||
    t.includes('questions to ask') ||
    t.includes('ask my supplier')
  )
}

function isSupplierSummaryAsk(t: string): boolean {
  if (isSupplierBrandsAsk(t)) return false
  if (isExplicitScorecardAsk(t)) return false
  return (
    t.includes('supplier summary') ||
    t.includes('summarize') ||
    (t.includes('summary') && t.includes('supplier')) ||
    t.includes('overview of') ||
    (t.includes('tell me about') && !t.includes('rating') && !t.includes('assessment'))
  )
}

function hasCountCue(t: string): boolean {
  return (
    t.includes('how many') ||
    t.includes('number of') ||
    /\bcounts?\b/.test(t) ||
    (/\btotal\b/.test(t) && t.includes('supplier'))
  )
}

function hasSupplierCue(t: string): boolean {
  return /\bsuppliers?\b/.test(t)
}

function hasGeoFilter(raw: string): boolean {
  const filters = extractSupplierListFilters(raw)
  return Boolean(
    filters.geoKind ||
      (filters.regionHint &&
        (filters.geoKind === 'region' ||
          filters.geoKind === 'country' ||
          (filters.locationHints && filters.locationHints.length > 0))) ||
      (filters.locationHints && filters.locationHints.length > 0)
  )
}

function hasGeoOrRiskFilter(raw: string): boolean {
  const filters = extractSupplierListFilters(raw)
  return Boolean(
    filters.riskBand ||
      filters.singleSource ||
      filters.categoryHint ||
      filters.geoKind ||
      filters.regionHint ||
      filters.procurementType ||
      (filters.locationHints && filters.locationHints.length > 0)
  )
}

/** Compare bands — not a filtered how-many in a place. */
function isCountByBandAsk(t: string): boolean {
  if (!hasCountCue(t) || !hasSupplierCue(t)) return false
  // Filtered "how many high in USA" / single-source / procurement → count_filtered
  const filters = extractSupplierListFilters(t)
  if (hasGeoFilter(t) || filters.singleSource || filters.procurementType || filters.categoryHint) {
    return false
  }
  const comparesBands =
    t.includes('vs') ||
    t.includes('versus') ||
    t.includes('risk band') ||
    (t.includes('high') && t.includes('medium')) ||
    (t.includes('high') && t.includes('low')) ||
    (t.includes('medium') && t.includes('low'))
  if (comparesBands) return true
  return (
    t.includes('high risk') ||
    t.includes('medium risk') ||
    t.includes('low risk') ||
    (t.includes('suppliers') && (t.includes('high') || t.includes('medium') || t.includes('low')))
  )
}

function isCountByRegionAsk(t: string): boolean {
  // Aggregate-only: "counts by region" — not "suppliers in USA"
  const aggregateCue =
    t.includes('by region') ||
    t.includes('per region') ||
    t.includes('by location') ||
    t.includes('counts by region') ||
    t.includes('count by region') ||
    t.includes('supplier counts by')
  if (!aggregateCue) return false
  // Avoid stealing list asks about suppliers in a country/region
  if (/\bsuppliers in\b/.test(t) && !t.includes('by region') && !t.includes('per region')) {
    return false
  }
  return (
    t.includes('count') ||
    t.includes('how many') ||
    t.includes('counts') ||
    t.includes('suppliers') ||
    t.includes('table')
  )
}

/** Filtered how-many: geography, category, single-source, and/or procurement (optional risk band). Unique suppliers. */
function isCountFilteredSuppliersAsk(raw: string, t: string): boolean {
  if (!hasCountCue(t) || !hasSupplierCue(t)) return false
  if (isCountByRegionAsk(t)) return false
  const filters = extractSupplierListFilters(raw)
  return Boolean(
    filters.singleSource ||
      filters.categoryHint ||
      filters.procurementType ||
      hasGeoFilter(raw)
  )
}

/** Portfolio size — unique total with no geo/risk filter. */
function isCountTotalSuppliersAsk(raw: string, t: string): boolean {
  if (!hasCountCue(t) || !hasSupplierCue(t)) return false
  if (isCountByRegionAsk(t)) return false
  if (hasGeoOrRiskFilter(raw)) return false
  // Band breakdowns handled by count_by_band
  if (
    t.includes('high') ||
    t.includes('medium') ||
    t.includes('low') ||
    t.includes('risk band')
  ) {
    return false
  }
  return true
}

function isHighRiskSupplierListAsk(t: string): boolean {
  // Leave aggregates to count tools
  if (hasCountCue(t)) return false
  if (/\bsingle[- ]source\b/.test(t) || /\bsole[- ]source\b/.test(t)) {
    return /\bsuppliers?\b/.test(t)
  }
  if (/\b(direct|indirect)(\s+procurement)?\b/.test(t) && /\bsuppliers?\b/.test(t)) {
    // Pivot / compare asks belong to group_suppliers_by, not a flat list
    if (
      /\b(break\s*down|breakdown|grouped?\s+by|group\s+by|table\s+by|vs|versus)\b/.test(t) ||
      /\bby\s+procurement\b/.test(t)
    ) {
      return false
    }
    return true
  }
  return (
    // "give/show a table of high risk … suppliers"
    /\b(give|show|list|find|name)\b[\w\s]*\btable\b[\w\s]*\b(high|medium|low)[- ]?risk\b/.test(t) ||
    /\btable of\b[\w\s]*\b(high|medium|low)[- ]?risk\b[\w\s]*\bsuppliers?\b/.test(t) ||
    // Allow words between risk and suppliers: "high risk packaging suppliers"
    /\b(high|medium|low)[- ]?risk\b[\w\s&/-]{0,40}\bsuppliers?\b/.test(t) ||
    /\b(high|medium|low)[- ]?risk suppliers\b/.test(t) ||
    /\bwhich suppliers (are|have)\b.+\brisk\b/.test(t) ||
    /\bwho are (the )?(high|medium|low)[- ]?risk\b/.test(t) ||
    /\bname (all |the )?(high|medium|low)[- ]?risk\b/.test(t) ||
    (/\bsuppliers in\b/.test(t) &&
      (t.includes('high') || t.includes('medium') || t.includes('low') || t.includes('risk'))) ||
    // Numeric score list asks
    (/\bsuppliers?\b/.test(t) &&
      /\b(financial|fin|operational|ops)\b/.test(t) &&
      /\b(more than|greater than|above|over|>|>=)\s*\d/.test(t))
  )
}

/** Multi-cut / pivot tables (risk × region × category × procurement). */
function isGroupSuppliersAsk(t: string): boolean {
  if (!hasSupplierCue(t) && !t.includes('table')) return false
  if (hasCountCue(t)) return false
  // Scorecard / ratings by parameter — not a portfolio pivot
  if (
    t.includes('by rating') ||
    t.includes('risk assessment') ||
    t.includes('scorecard') ||
    (t.includes('rating') && t.includes('table'))
  ) {
    return false
  }

  const multi =
    (t.includes('risk') && t.includes('region')) ||
    (t.includes('risk') && (t.includes('category') || t.includes('categories'))) ||
    ((t.includes('category') || t.includes('categories')) && t.includes('region')) ||
    (/\bprocurement\b/.test(t) &&
      (t.includes('risk') || t.includes('region') || t.includes('category')))

  const cue =
    t.includes('table by') ||
    t.includes('grouped by') ||
    t.includes('group by') ||
    t.includes('broken down') ||
    t.includes('break down') ||
    t.includes('breakdown') ||
    /\bby\s+(risk|category|categories|region|procurement)\b/.test(t) ||
    /\bdirect\s+(vs|versus|and)\s+indirect\b/.test(t)

  if (!cue && !(multi && (t.includes('by ') || t.includes('table')))) return false

  // Keep simple region rollups on count_by_region
  if (
    !multi &&
    (t.includes('count by region') ||
      t.includes('counts by region') ||
      t.includes('supplier counts by region') ||
      t.includes('counts by region'))
  ) {
    return false
  }
  if (!multi && t.includes('by region') && hasCountCue(t) && !t.includes('table by')) {
    return false
  }

  // Require at least one portfolio dimension (risk/region/category/procurement)
  if (
    !/\b(risk|region|regions|categor(?:y|ies)|industry|procurement|direct|indirect)\b/.test(t) &&
    !multi
  ) {
    return false
  }

  return true
}

function isSpendAsk(t: string): boolean {
  return /\bspend\b/.test(t)
}

function cleanSpendName(raw: string): string | null {
  const name = raw.replace(/[?.!]+$/g, '').trim()
  if (name.length < 2) return null
  const n = name.toLowerCase()
  if (/^(top|our|the|my|all|this|that|where|what|how|show|high|spend)\b/.test(n)) return null
  if (/^(suppliers?|categor(?:y|ies)|portfolio|cube|risk)$/.test(n)) return null
  return name
}

function extractSpendCategory(raw: string): string | null {
  const patterns = [
    /\bspend\s+(?:in|for|on)\s+(?:the\s+)?category\s+(.+)$/i,
    /\b(?:category|roll\s*-?\s*up(?:\s*2)?)\s+(.+?)\s+spend\b/i,
    /\b(?:in|for|on)\s+(?:the\s+)?(?:category|roll\s*-?\s*up(?:\s*2)?)\s+(.+)$/i,
  ]
  for (const re of patterns) {
    const m = raw.match(re)
    if (m?.[1]) {
      const name = m[1].replace(/[?.!]+$/g, '').trim()
      if (name && !/^spend\b/i.test(name)) return name
    }
  }
  return null
}

function extractSpendSupplierName(raw: string): string | null {
  const patterns = [
    /\bspend\s+(?:with|for|on)\s+(.+)$/i,
    /\b(?:what is|what's|show)\s+(.+?)(?:'s|s')?\s+spend\b/i,
  ]
  for (const re of patterns) {
    const m = raw.match(re)
    if (m?.[1]) {
      const name = cleanSpendName(m[1])
      if (name) return name
    }
  }
  return null
}

function isMitigationsForSupplierAsk(t: string): boolean {
  if (!t.includes('mitigation')) return false
  if (t.includes('tracker') && !extractSupplierNameCandidate(t)) return false
  // "open mitigations for X" / "mitigations for CHEP"
  return (
    (/\bmitigations?\s+for\b/.test(t) ||
      /\bfor\b.+\bmitigations?\b/.test(t) ||
      (t.includes('open mitigation') && Boolean(extractSupplierNameCandidate(t)))) &&
    !t.includes('active mitigations in a table') // keep global active list
  )
}

/**
 * Keyword route for Risk Analyst. Order matches tool priority in the DB answerer.
 */
export function classifyEarlySignalsIntent(userText: string): RiskAnalystClassification {
  const raw = userText.trim()
  if (!raw) return { kind: 'fallthrough' }
  const t = raw.toLowerCase()
  const supplierName = extractSupplierNameCandidate(raw)

  if (isSupplierBrandsAsk(raw)) {
    return { kind: 'tool', tool: 'supplier_brands', supplierName }
  }

  if (isNextBestAsk(t)) {
    return { kind: 'narrative', tool: 'next_best_actions', supplierName }
  }

  if (isQuestionsToAsk(t)) {
    return { kind: 'narrative', tool: 'questions_to_ask', supplierName }
  }

  if (isDimensionCommentaryAsk(raw)) {
    return { kind: 'tool', tool: 'supplier_dimension_commentary', supplierName }
  }

  const besideDimension = supplierBesideDimension(raw)
  if (besideDimension) {
    return { kind: 'tool', tool: 'supplier_dimension_commentary', supplierName: besideDimension }
  }

  const urgentName = urgentSupplierName(raw)
  if (urgentName) {
    return { kind: 'narrative', tool: 'next_best_actions', supplierName: urgentName }
  }

  if (isHighestRiskQuestion(raw)) {
    const scope = highestRiskScope(raw)
    const category = scope ? categoryKeyForPhrase(scope) : null
    if (category) {
      return {
        kind: 'tool',
        tool: 'highest_in_category',
        supplierName: null,
        categoryName: category,
      }
    }
    return {
      kind: 'tool',
      tool: 'supplier_highest_areas',
      supplierName: scope,
    }
  }

  const roleCategory = categoryFromRoleAsk(raw)
  if (roleCategory) {
    return {
      kind: 'tool',
      tool: 'category_next_action',
      supplierName: null,
      categoryName: roleCategory,
    }
  }

  if (isMitigationsForSupplierAsk(t)) {
    return { kind: 'tool', tool: 'mitigations_for_supplier', supplierName }
  }

  if (isSpendAsk(t)) {
    const categoryName = extractSpendCategory(raw)
    if (categoryName) {
      return { kind: 'tool', tool: 'spend_category', supplierName: null, categoryName }
    }
    const spendSupplier = extractSpendSupplierName(raw)
    if (spendSupplier) {
      return { kind: 'tool', tool: 'spend_supplier', supplierName: spendSupplier }
    }
    return { kind: 'tool', tool: 'spend_portfolio', supplierName: null }
  }

  // Prefer supplier list (e.g. high-risk in USA) over region rollups
  if (isHighRiskSupplierListAsk(t)) {
    return { kind: 'tool', tool: 'list_suppliers', supplierName: null }
  }

  // Multi-cut grouped tables before simple region counts
  if (isGroupSuppliersAsk(t)) {
    return { kind: 'tool', tool: 'group_suppliers_by', supplierName: null }
  }

  // Aggregates: region rollup → filtered how-many → band breakdown → portfolio total
  if (isCountByRegionAsk(t)) {
    return { kind: 'tool', tool: 'count_by_region', supplierName: null }
  }

  if (isCountFilteredSuppliersAsk(raw, t)) {
    return { kind: 'tool', tool: 'count_filtered_suppliers', supplierName: null }
  }

  if (isCountByBandAsk(t)) {
    return { kind: 'tool', tool: 'count_by_band', supplierName: null }
  }

  if (isCountTotalSuppliersAsk(raw, t)) {
    return { kind: 'tool', tool: 'count_total_suppliers', supplierName: null }
  }

  if (isSupplierSummaryAsk(t) && (supplierName || t.includes('tell me about') || t.includes('summary'))) {
    return { kind: 'tool', tool: 'supplier_summary', supplierName }
  }

  // Scorecard / ratings (named or explicit ask)
  if (supplierName && isSupplierScorecardAsk(raw)) {
    return { kind: 'tool', tool: 'supplier_scorecard', supplierName }
  }
  if (!supplierName && isExplicitScorecardAsk(raw)) {
    return { kind: 'tool', tool: 'supplier_scorecard', supplierName: null }
  }

  const list = matchListTableIntent(raw)
  if (list === 'active_mitigations') {
    return { kind: 'tool', tool: 'list_active_mitigations', supplierName: null }
  }
  if (list === 'alerts') {
    return { kind: 'tool', tool: 'list_alerts', supplierName: null }
  }
  if (list === 'intelligence') {
    return { kind: 'tool', tool: 'list_suppliers', supplierName: null }
  }
  if (list === 'mitigation_tracker') {
    return { kind: 'tool', tool: 'list_tracker', supplierName: null }
  }
  if (list === 'special_reports') {
    return { kind: 'tool', tool: 'list_reports', supplierName }
  }

  const bareName = bareSupplierNameCandidate(raw)
  if (bareName) {
    return { kind: 'tool', tool: 'supplier_summary', supplierName: bareName }
  }

  return { kind: 'fallthrough' }
}

export const RISK_ANALYST_INTENT_CATALOG: Array<RiskAnalystTool | RiskAnalystNarrative | 'fallthrough'> =
  [
    'supplier_brands',
    'supplier_scorecard',
    'supplier_dimension_commentary',
    'list_active_mitigations',
    'list_alerts',
    'list_suppliers',
    'list_tracker',
    'list_reports',
    'supplier_summary',
    'count_by_band',
    'count_by_region',
    'count_total_suppliers',
    'count_filtered_suppliers',
    'group_suppliers_by',
    'mitigations_for_supplier',
    'supplier_highest_areas',
    'highest_in_category',
    'category_next_action',
    'next_best_actions',
    'questions_to_ask',
    'fallthrough',
  ]
